import React, { useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw, Pencil, Trash2, ChevronUp, ChevronDown, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { adminAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';

interface MetaConfig {
  id?: number;
  name: string;
  app_id: string;
  app_secret: string;
  access_token: string;
  access_token_masked?: string;
  ad_account_id: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

const emptyConfig: MetaConfig = { name: 'Default', app_id: '', app_secret: '', access_token: '', ad_account_id: '', is_active: true };

const MetaAdsConfig: React.FC = () => {
  const [configs, setConfigs] = useState<MetaConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<MetaConfig | null>(null);
  const [formData, setFormData] = useState<MetaConfig>(emptyConfig);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState<number | null>(null);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({ show: false, message: '', type: 'success' });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [accountInfo, setAccountInfo] = useState<any>(null);

  useEffect(() => { loadConfigs(); }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getMetaAdsConfig();
      let data = response.data || response;
      if (data?.results && Array.isArray(data.results)) data = data.results;
      setConfigs(Array.isArray(data) ? data : (data ? [data] : []));
    } catch (error) { console.error('Failed to load Meta Ads configs:', error); setConfigs([]); }
    finally { setLoading(false); }
  };

  const handleAddNew = () => { setSelectedConfig(null); setFormData(emptyConfig); setAccountInfo(null); setIsFormOpen(true); };
  const handleEdit = (config: MetaConfig) => { setSelectedConfig(config); setFormData({ ...config, access_token: '', app_secret: '' }); setAccountInfo(null); setIsFormOpen(true); };
  const handleDeleteClick = (config: MetaConfig) => { setSelectedConfig(config); setIsDeleteDialogOpen(true); };

  const handleDelete = async () => {
    if (!selectedConfig) return;
    try {
      await adminAPI.deleteMetaAdsConfig(selectedConfig.id!);
      setToast({ show: true, message: 'Configuration deleted successfully', type: 'success' });
      loadConfigs();
    } catch (error) {
      setToast({ show: true, message: 'Failed to delete configuration', type: 'error' });
    } finally { setIsDeleteDialogOpen(false); setSelectedConfig(null); }
  };

  const handleTestConnection = async (config: MetaConfig) => {
    try {
      setTestingConnection(config.id!);
      const response = await adminAPI.testMetaAdsConnection(config.id!);
      const data = response.data || response;
      if (data.status === 'success') {
        setToast({ show: true, message: data.message + ' — Account: ' + data.account?.name, type: 'success' });
        setAccountInfo(data.account);
      } else { setToast({ show: true, message: data.message, type: 'error' }); }
    } catch (error: any) {
      setToast({ show: true, message: error.response?.data?.message || 'Failed to test connection', type: 'error' });
    } finally { setTestingConnection(null); }
  };

  const handleSave = async () => {
    if (!formData.ad_account_id) { setToast({ show: true, message: 'Ad Account ID is required', type: 'error' }); return; }
    if (!selectedConfig && !formData.access_token) { setToast({ show: true, message: 'Access Token is required', type: 'error' }); return; }
    try {
      setSaving(true);
      const payload: any = { ...formData };
      if (selectedConfig?.id && !payload.access_token) delete payload.access_token;
      if (!payload.app_secret) delete payload.app_secret;
      if (selectedConfig?.id) { await adminAPI.updateMetaAdsConfig(selectedConfig.id, payload); setToast({ show: true, message: 'Configuration updated successfully', type: 'success' }); }
      else { await adminAPI.createMetaAdsConfig(payload); setToast({ show: true, message: 'Configuration created successfully', type: 'success' }); }
      setIsFormOpen(false); setSelectedConfig(null); loadConfigs();
    } catch (error: any) {
      const detail = error.response?.data;
      let msg = 'Failed to save configuration';
      if (detail && typeof detail === 'object') { const first = Object.values(detail).flat()[0]; if (first) msg = String(first); }
      setToast({ show: true, message: msg, type: 'error' });
    } finally { setSaving(false); }
  };

  const handleSort = (f: string) => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('asc'); } setCurrentPage(1); };
  const SA = ({ field }: { field: string }) => sortField !== field ? null : sortDir === 'asc' ? <ChevronUp size={10} className="adm-sort-arrow" /> : <ChevronDown size={10} className="adm-sort-arrow" />;

  const sorted = useMemo(() => [...configs].sort((a: any, b: any) => sortDir === 'asc' ? String(a[sortField] ?? '').localeCompare(String(b[sortField] ?? '')) : String(b[sortField] ?? '').localeCompare(String(a[sortField] ?? ''))), [configs, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paginated = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const labelStyle: React.CSSProperties = { display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '13px', color: '#374151' };
  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' };
  const helpStyle: React.CSSProperties = { fontSize: '11px', color: '#9ca3af', marginTop: '2px' };

  return (
    <div className="admin-page adm-page">
      <div className="adm-topbar">
        <h1 className="adm-page-title">Meta Ads Config</h1>
        <div className="adm-topbar-right">
          <button className="adm-btn adm-btn-add" onClick={handleAddNew}><Plus size={13} /> Add Config <span className="adm-btn-badge">{configs.length}</span></button>
          <button className="adm-btn adm-btn-refresh" onClick={loadConfigs} disabled={loading}>{loading ? <span className="adm-spin"><Loader2 size={13} /></span> : <RefreshCw size={13} />} Refresh</button>
        </div>
      </div>
      <div className="adm-table-card">
        <div className="adm-table-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('name')}>Name <SA field="name" /></th>
                <th className="adm-th">App ID</th>
                <th className="adm-th">Ad Account ID</th>
                <th className="adm-th">Access Token</th>
                <th className="adm-th adm-th-sort" onClick={() => handleSort('is_active')}>Status <SA field="is_active" /></th>
                <th className="adm-th" style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="adm-loading"><span className="adm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>Loading...</td></tr>}
              {!loading && paginated.length === 0 && <tr><td colSpan={6} className="adm-empty">No Meta Ads configurations found.</td></tr>}
              {!loading && paginated.map((c: MetaConfig) => (
                <tr className="adm-row" key={c.id}>
                  <td className="adm-td"><span className="adm-name-main">{c.name}</span></td>
                  <td className="adm-td"><span className="adm-mono">{c.app_id || '-'}</span></td>
                  <td className="adm-td"><span className="adm-mono" style={{ color: '#4f46e5' }}>{c.ad_account_id}</span></td>
                  <td className="adm-td"><span className="adm-mono" style={{ color: '#888' }}>{c.access_token_masked || '-'}</span></td>
                  <td className="adm-td"><span className={'adm-chip ' + (c.is_active ? 'adm-chip-green' : 'adm-chip-red')}>{c.is_active ? <><CheckCircle2 size={9} /> Active</> : <><XCircle size={9} /> Inactive</>}</span></td>
                  <td className="adm-td" style={{ padding: '6px 4px' }}>
                    <div className="adm-actions-row">
                      <button className="adm-action-btn adm-ab-approve" style={{ fontSize: 10, padding: '0 6px', width: 'auto', minWidth: 36 }} onClick={() => handleTestConnection(c)} disabled={testingConnection === c.id} title="Test">{testingConnection === c.id ? <span className="adm-spin"><Loader2 size={10} /></span> : 'Test'}</button>
                      <button className="adm-action-btn adm-ab-edit" onClick={() => handleEdit(c)} title="Edit"><Pencil size={12} /></button>
                      <button className="adm-action-btn adm-ab-del" onClick={() => handleDeleteClick(c)} title="Delete"><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="adm-pagination">
          <div className="adm-page-size-wrap"><span>Show</span><select className="adm-page-size-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>{[10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}</select><span>per page</span></div>
          <div className="adm-page-info">{sorted.length === 0 ? '0 configs' : ((currentPage - 1) * pageSize + 1) + '-' + Math.min(currentPage * pageSize, sorted.length) + ' of ' + sorted.length}</div>
          <div className="adm-page-controls">
            <button className="adm-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>&lsaquo;</button>
            <button className="adm-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>&rsaquo;</button>
          </div>
        </div>
      </div>

      {isFormOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '28px', width: '520px', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #e5e7eb', paddingBottom: '12px' }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '18px' }}>{selectedConfig ? 'Edit Meta Ads Config' : 'New Meta Ads Config'}</h3>
              <button onClick={() => setIsFormOpen(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>x</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div><label style={labelStyle}>Config Name</label><input style={inputStyle} value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., My Store Ads" /></div>
              <div><label style={labelStyle}>Meta App ID <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label><input style={inputStyle} value={formData.app_id} onChange={e => setFormData({ ...formData, app_id: e.target.value })} placeholder="e.g., 123456789" /><div style={helpStyle}>From developers.facebook.com</div></div>
              <div><label style={labelStyle}>App Secret <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label><input style={inputStyle} type="password" value={formData.app_secret} onChange={e => setFormData({ ...formData, app_secret: e.target.value })} placeholder={selectedConfig ? '(leave blank to keep current)' : 'Enter app secret'} /></div>
              <div><label style={labelStyle}>Ad Account ID <span style={{ color: '#dc2626' }}>*</span></label><input style={inputStyle} value={formData.ad_account_id} onChange={e => setFormData({ ...formData, ad_account_id: e.target.value })} placeholder="e.g., act_123456789" /><div style={helpStyle}>Include the act_ prefix.</div></div>
              <div><label style={labelStyle}>Access Token <span style={{ color: '#dc2626' }}>*</span></label><textarea style={{ ...inputStyle, minHeight: '80px', fontFamily: 'monospace', fontSize: '12px' }} value={formData.access_token} onChange={e => setFormData({ ...formData, access_token: e.target.value })} placeholder={selectedConfig ? '(leave blank to keep current)' : 'Paste your long-lived access token here'} /><div style={helpStyle}>Generate from Graph API Explorer with ads_read permission.</div></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><input type="checkbox" id="is_active" checked={formData.is_active} onChange={e => setFormData({ ...formData, is_active: e.target.checked })} /><label htmlFor="is_active" style={{ fontSize: '14px', color: '#374151', cursor: 'pointer' }}>Active</label></div>
              {accountInfo && (<div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px' }}><div style={{ fontWeight: 600, color: '#166534', marginBottom: '4px' }}>Connected Account</div><div style={{ fontSize: '13px', color: '#15803d' }}><div><strong>Name:</strong> {accountInfo.name}</div><div><strong>Currency:</strong> {accountInfo.currency}</div><div><strong>Status:</strong> {accountInfo.status}</div></div></div>)}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
              <button onClick={() => setIsFormOpen(false)} style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: '14px' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: saving ? '#93c5fd' : '#2563eb', color: 'white', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: 600 }}>{saving ? 'Saving...' : (selectedConfig ? 'Update' : 'Create')}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog isOpen={isDeleteDialogOpen} title="Delete Meta Ads Configuration" message={'Are you sure you want to delete this configuration?'} onConfirm={handleDelete} onClose={() => setIsDeleteDialogOpen(false)} confirmText="Delete" cancelText="Cancel" type="danger" />
      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default MetaAdsConfig;